import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router";
import { IconButton, Link as MuiLink } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useSnackbar } from "notistack";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import SparteEditDialog from "../modals/SparteEditDialog.jsx";

export default function SpartenTable() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const [activeFilterTab, setActiveFilterTab] = useState("all");
  const [editedSparte, setEditedSparte] = useState(null);

  const tableKey = "sparten";
  const storageKey = "v2_table_sparten";
  const defaultRowsPerPage = getUserRowsPerPage(user, 10);

  const tablesConfig = useMemo(() => {
    const filters = {};

    if (activeFilterTab === "with_form") {
      filters.has_claim_form = "1";
    } else if (activeFilterTab === "no_form") {
      filters.has_claim_form = "0";
    }

    const extraParams = {
      sort: [{ field: "name", order: "asc" }],
    };

    if (Object.keys(filters).length > 0) {
      extraParams.filters = filters;
    }

    return [
      {
        key: tableKey,
        label: "Sparten",
        route: "data/sparten",
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey,
        extraParams,
      },
    ];
  }, [activeFilterTab, defaultRowsPerPage, storageKey]);

  const { tableStates, updateTableState } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];

  useEffect(() => {
    updateTableState(tableKey, { pageIndex: 0, shouldFetch: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilterTab]);

  const filterTabs = [
    { key: "all", label: "Alle", onClick: setActiveFilterTab },
    { key: "with_form", label: "Mit Formular", onClick: setActiveFilterTab },
    { key: "no_form", label: "Ohne Formulare", onClick: setActiveFilterTab },
  ];

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      size: 250,
    },
    {
      accessorKey: "beschreibung",
      header: "Beschreibung",
      enableSorting: false,
      Cell: ({ cell }) => cell.getValue() ?? "–",
    },
    {
      accessorFn: (row) => row.claim_form?.label ?? "–",
      accessorKey: "claim_form_label",
      header: "Schadenformular",
      enableSorting: false,
      size: 200,
      Cell: ({ row }) => {
        const claimForm = row.original.claim_form;
        if (!claimForm) {
          return "–";
        }
        return (
          <MuiLink
            component={RouterLink}
            to={`/admin/claim-forms/${claimForm.id}`}
          >
            {claimForm.label}
          </MuiLink>
        );
      },
    },
    {
      id: "actions",
      header: "Aktionen",
      size: 80,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) =>
        row.original.can_update ? (
          <IconButton
            size="small"
            title="Beschreibung bearbeiten"
            onClick={() => setEditedSparte(row.original)}
          >
            <EditOutlinedIcon />
          </IconButton>
        ) : null,
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        initialState={{ showGlobalFilter: true }}
        isLoading={tableState.isLoading}
        hasLoaded={tableState.hasLoaded}
        pageIndex={tableState.pageIndex}
        pageSize={tableState.pageSize}
        totalRows={tableState.totalRows}
        onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
        onChangePage={(nextOrUpdater) => {
          const base = {
            pageIndex: tableState.pageIndex ?? 0,
            pageSize: tableState.pageSize ?? defaultRowsPerPage,
          };
          const next =
            typeof nextOrUpdater === "function"
              ? nextOrUpdater(base)
              : nextOrUpdater;
          if (!next) {
            return;
          }
          updateTableState(tableKey, {
            pageIndex: next.pageIndex,
            pageSize: next.pageSize,
          });
        }}
        onChangeGlobalFilter={(searchword) =>
          updateTableState(tableKey, { searchword })
        }
        filterTabs={filterTabs}
        activeFilterTab={activeFilterTab}
        storageKey={storageKey}
        enableHeader
        enableSorting
        enableGlobalFilter={true}
        enableColumnFilters={false}
        enableColumnHiding={true}
      />
      <SparteEditDialog
        open={!!editedSparte}
        sparte={editedSparte}
        onClose={() => setEditedSparte(null)}
        onSaved={() => {
          setEditedSparte(null);
          updateTableState(tableKey, { shouldFetch: true });
        }}
      />
    </>
  );
}
